import React, { useState, useEffect } from 'react';
import {
  Plus, Pencil, Trash2, AlertTriangle, Sparkles,
} from 'lucide-react';
import { contextApi } from '../api/services';
import type { ExecutiveContext, ContextLayer } from '../types/models';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { useToast } from '@/components/ui/toast';

// ---------------------------------------------------------------------------
// ContextPage — the multi-layer executive context store fed into every AI
// planning call. Layer tabs + tone-coloured entry cards + a "Get Started"
// wizard that pre-fills a context entry from suggestion templates.
// ---------------------------------------------------------------------------

const layers: ContextLayer[] = ['Profile', 'Strategic', 'Weekly', 'Daily', 'AnnualStrategy'];

const layerSuggestions: Record<string, Array<{ key: string; value: string }>> = {
  Profile: [
    { key: 'Leadership Style', value: 'I prefer direct, data-driven communication with clear action items' },
    { key: 'Decision Style', value: 'I want options presented with a recommendation, not open-ended' },
    { key: 'Communication Preferences', value: 'Brief updates over email, detailed discussion in person' },
    { key: 'Working Style', value: 'Deep work mornings, meetings afternoons' },
  ],
  Strategic: [
    { key: 'Q2 Focus', value: 'Customer retention and product-led growth' },
    { key: 'Board Priority', value: 'Revenue growth 20% YoY, margin improvement' },
    { key: 'Risk Awareness', value: 'Regulatory changes in Q3, key person dependency' },
  ],
  Weekly: [
    { key: "This Week's Focus", value: 'Finalise Q2 board pack, review vendor shortlist' },
    { key: 'Key Meetings', value: 'Board prep Monday, all-hands Wednesday' },
    { key: 'Blockers', value: 'Waiting on legal review for contract' },
  ],
  Daily: [
    { key: "Today's Priority", value: 'Complete strategy review, send board update' },
    { key: 'Energy Level', value: 'High energy morning, flagging after lunch' },
    { key: 'Context for EA', value: 'Running late from school drop-off, push 9am to 9:15' },
  ],
};

const CONTEXT_TEMPLATES: Record<string, { key: string; label: string; placeholder: string }[]> = {
  Profile: [
    { key: 'leadership_style', label: 'Leadership Style', placeholder: 'Describe your leadership approach...' },
    { key: 'decision_style', label: 'Decision Making Style', placeholder: 'How you prefer to make decisions...' },
    { key: 'communication_pref', label: 'Communication Preferences', placeholder: 'How you prefer to receive information...' },
    { key: 'work_style', label: 'Work Style', placeholder: 'Your preferred working patterns...' },
    { key: 'pet_peeves', label: 'Pet Peeves', placeholder: 'Things that frustrate you in a business context...' },
  ],
  Strategic: [
    { key: 'market_position', label: 'Market Position', placeholder: 'Current competitive position...' },
    { key: 'key_relationships', label: 'Key Stakeholder Relationships', placeholder: 'Important relationships and dynamics...' },
    { key: 'board_priorities', label: 'Board Priorities', placeholder: 'What the board is focused on...' },
    { key: 'competitive_threats', label: 'Competitive Threats', placeholder: 'Current competitive landscape...' },
    { key: 'growth_levers', label: 'Growth Levers', placeholder: 'Key growth opportunities...' },
  ],
  Weekly: [
    { key: 'this_week_focus', label: "This Week's Focus", placeholder: 'Top priorities for this week...' },
    { key: 'key_meetings', label: 'Key Meetings This Week', placeholder: 'Important meetings and their context...' },
    { key: 'blockers', label: 'Current Blockers', placeholder: "What's blocking progress..." },
  ],
  Daily: [
    { key: 'energy_level', label: 'Energy Level', placeholder: "How you're feeling today..." },
    { key: 'top_priority', label: "Today's Top Priority", placeholder: 'The one thing that must get done...' },
    { key: 'mood_context', label: 'Mood / Context', placeholder: 'Anything affecting your mindset...' },
  ],
};

const layerColors: Record<ContextLayer, string> = {
  Profile: '#6C9BFF',
  Strategic: '#69F0AE',
  Weekly: '#FFB74D',
  Daily: '#FF8A65',
  AnnualStrategy: '#CE93D8',
};

const layerDescriptions: Record<ContextLayer, string> = {
  Profile: 'Your leadership identity, management style, and communication preferences',
  Strategic: 'Quarterly and annual priorities that guide planning decisions',
  Weekly: "This week's focus areas, key meetings, and short-term objectives",
  Daily: "Today's specific context, energy level, and immediate concerns",
  AnnualStrategy: 'Long-term strategic themes and organisational direction for the year',
};

const QUICK_NONE = '__none__';

function isExpiringSoon(expiryDate?: string): boolean {
  if (!expiryDate) return false;
  const expiry = new Date(expiryDate);
  const now = new Date();
  const diff = expiry.getTime() - now.getTime();
  const days = diff / (1000 * 60 * 60 * 24);
  return days >= 0 && days <= 7;
}

function isExpired(expiryDate?: string): boolean {
  if (!expiryDate) return false;
  return new Date(expiryDate) < new Date();
}

export default function ContextPage() {
  const confirm = useConfirm();
  const { toast } = useToast();
  const [contexts, setContexts] = useState<ExecutiveContext[]>([]);
  const [loading, setLoading] = useState(true); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editCtx, setEditCtx] = useState<Partial<ExecutiveContext> | null>(null);
  const [tabValue, setTabValue] = useState<'all' | ContextLayer>('all');
  const [activeOnly, setActiveOnly] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardLayer, setWizardLayer] = useState<string>('Profile');

  const loadContexts = async () => {
    setLoading(true);
    try {
      setContexts(await contextApi.getAll());
    } catch (err) {
      // silent-by-design: page load; empty list signals failure.
      console.error('Failed to load contexts', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadContexts(); }, []);

  const handleSave = async () => {
    if (!editCtx?.key || !editCtx?.value) return;
    try {
      if (editCtx.id) {
        await contextApi.update(editCtx.id, editCtx);
      } else {
        await contextApi.create(editCtx);
      }
      setDialogOpen(false);
      setEditCtx(null);
      loadContexts();
    } catch (err: any) {
      console.error('Failed to save context', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to save context entry' });
    }
  };

  const handleDelete = async (id: string) => {
    const ok = await confirm({
      title: 'Delete context entry?',
      body: 'This entry will be removed from your executive context. Future AI planning calls will no longer see it.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await contextApi.delete(id);
      loadContexts();
    } catch (err: any) {
      console.error('Failed to delete', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to delete context entry' });
    }
  };

  const handleWizardSelect = (suggestion: { key: string; value: string }) => {
    setEditCtx({ layer: wizardLayer as ContextLayer, key: suggestion.key, value: suggestion.value, isActive: true });
    setWizardOpen(false);
    setDialogOpen(true);
  };

  const currentLayer = tabValue !== 'all' ? (tabValue as ContextLayer) : null;
  const quickAddTemplates = currentLayer && layerSuggestions[currentLayer] ? layerSuggestions[currentLayer] : null;

  const baseFiltered = tabValue === 'all'
    ? contexts
    : contexts.filter(c => c.layer === tabValue);
  const filteredContexts = activeOnly ? baseFiltered.filter(c => c.isActive) : baseFiltered;

  const layerTemplates = currentLayer && CONTEXT_TEMPLATES[currentLayer] ? CONTEXT_TEMPLATES[currentLayer] : null;
  const showTemplateCards = layerTemplates && currentLayer && baseFiltered.length === 0;

  const handleTemplateClick = (template: { key: string; label: string; placeholder: string }) => {
    if (!currentLayer) return;
    setEditCtx({ layer: currentLayer, key: template.label, value: '', isActive: true });
    setDialogOpen(true);
  };

  return (
    <div className="mx-auto max-w-[1200px] p-4 md:p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Context Engine</h1>
          <p className="text-sm text-muted-foreground">Executive context used in all AI planning calls</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setWizardOpen(true)} className="gap-1.5">
            <Sparkles className="h-4 w-4" />
            Get Started
          </Button>
          <Button
            onClick={() => {
              setEditCtx({ layer: 'Daily', key: '', value: '', isActive: true });
              setDialogOpen(true);
            }}
            className="gap-1.5"
          >
            <Plus className="h-4 w-4" />
            Add Context
          </Button>
        </div>
      </div>

      <Alert variant="info" className="mb-6">
        <AlertDescription>
          The Context Engine feeds into AI planning. The more context you provide, the better Cadence plans
          your day. Strategic context shapes weekly planning. Daily context adjusts today's schedule.
        </AlertDescription>
      </Alert>

      <Tabs
        value={tabValue}
        onValueChange={(v) => setTabValue(v as 'all' | ContextLayer)}
        className="mb-2"
      >
        <TabsList className="overflow-x-auto">
          <TabsTrigger value="all">All ({contexts.length})</TabsTrigger>
          {layers.map(l => (
            <TabsTrigger key={l} value={l}>
              {l} ({contexts.filter(c => c.layer === l).length})
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {currentLayer && (
        <p className="mb-3 text-sm italic text-muted-foreground">{layerDescriptions[currentLayer]}</p>
      )}

      {/* Template cards */}
      {showTemplateCards && layerTemplates && currentLayer && (
        <div className="mb-4">
          <p className="mb-2 text-sm font-semibold text-muted-foreground">Get started with templates:</p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3">
            {layerTemplates.map((tpl) => (
              <button
                key={tpl.key}
                type="button"
                onClick={() => handleTemplateClick(tpl)}
                className="rounded-2xl border-2 border-dashed p-3 text-left transition-all"
                style={{
                  borderColor: `${layerColors[currentLayer]}60`,
                  backgroundColor: 'transparent',
                }}
              >
                <div className="flex items-center gap-2">
                  <Plus className="h-4 w-4 shrink-0" style={{ color: layerColors[currentLayer] }} />
                  <div>
                    <p className="text-sm font-semibold">{tpl.label}</p>
                    <p className="text-xs text-muted-foreground">{tpl.placeholder}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Quick-add + active-only */}
      <div className="mb-3 flex items-center justify-between gap-3">
        {quickAddTemplates ? (
          <Select
            value={QUICK_NONE}
            onValueChange={(v) => {
              if (v === QUICK_NONE) return;
              const tpl = quickAddTemplates.find(t => t.key === v);
              if (tpl && currentLayer) {
                setEditCtx({ layer: currentLayer, key: tpl.key, value: tpl.value, isActive: true });
                setDialogOpen(true);
              }
            }}
          >
            <SelectTrigger className="w-[240px]">
              <SelectValue placeholder="Quick-add template" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={QUICK_NONE}>Quick-add template…</SelectItem>
              {quickAddTemplates.map(tpl => (
                <SelectItem key={tpl.key} value={tpl.key}>{tpl.key}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : <div />}
        <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
          <Checkbox checked={activeOnly} onCheckedChange={(c) => setActiveOnly(!!c)} />
          Active only
        </label>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {filteredContexts.map((ctx) => (
          <Card
            key={ctx.id}
            className="border-l-[3px]"
            style={{ borderLeftColor: layerColors[ctx.layer] }}
          >
            <CardContent>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex flex-wrap items-center gap-1.5">
                    <span
                      className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium"
                      style={{ backgroundColor: `${layerColors[ctx.layer]}20`, color: layerColors[ctx.layer] }}
                    >
                      {ctx.layer}
                    </span>
                    <p className="text-sm font-semibold">{ctx.key}</p>
                    {!ctx.isActive && (
                      <span className="inline-flex items-center rounded-full border border-border bg-transparent px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                        Inactive
                      </span>
                    )}
                    {isExpiringSoon(ctx.expiryDate) && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-400">
                        <AlertTriangle className="h-3 w-3" />
                        Expires {new Date(ctx.expiryDate!).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                      </span>
                    )}
                    {isExpired(ctx.expiryDate) && (
                      <span className="inline-flex items-center rounded-full bg-rose-500/15 px-2 py-0.5 text-[10px] font-medium text-rose-600 dark:text-rose-400">
                        Expired
                      </span>
                    )}
                  </div>
                  <p className="whitespace-pre-line text-sm">{ctx.value}</p>
                </div>
                <div className="flex shrink-0 gap-0.5">
                  <Button size="icon-sm" variant="ghost" onClick={() => { setEditCtx(ctx); setDialogOpen(true); }} aria-label="Edit">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button size="icon-sm" variant="ghost" onClick={() => handleDelete(ctx.id)} aria-label="Delete">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit / new dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => !o && setDialogOpen(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editCtx?.id ? 'Edit Context' : 'New Context'}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ctx-layer">Layer</Label>
              <Select
                value={editCtx?.layer || 'Daily'}
                onValueChange={(v) => setEditCtx(prev => ({ ...prev, layer: v as ContextLayer }))}
              >
                <SelectTrigger id="ctx-layer"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {layers.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ctx-key">Key</Label>
              <Input
                id="ctx-key"
                value={editCtx?.key || ''}
                onChange={(e) => setEditCtx(prev => ({ ...prev, key: e.target.value }))}
                placeholder="e.g., Current Focus, Team Priority"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ctx-value">Value</Label>
              <Textarea
                id="ctx-value"
                rows={4}
                value={editCtx?.value || ''}
                onChange={(e) => setEditCtx(prev => ({ ...prev, value: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Guided wizard */}
      <Dialog open={wizardOpen} onOpenChange={(o) => !o && setWizardOpen(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Get Started with Context</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <p className="text-sm text-muted-foreground">
              Choose a layer, then select a suggestion to pre-fill a context entry. You can customise
              the value before saving.
            </p>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="wiz-layer">Layer</Label>
              <Select value={wizardLayer} onValueChange={setWizardLayer}>
                <SelectTrigger id="wiz-layer"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.keys(layerSuggestions).map(l => (
                    <SelectItem key={l} value={l}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-sm font-semibold">Suggestions for {wizardLayer}</p>
            <div className="flex flex-col gap-1">
              {(layerSuggestions[wizardLayer] || []).map(suggestion => (
                <button
                  key={suggestion.key}
                  type="button"
                  onClick={() => handleWizardSelect(suggestion)}
                  className="rounded-md border border-border p-2 text-left transition-colors hover:bg-muted/40"
                >
                  <p className="text-sm font-medium">{suggestion.key}</p>
                  <p className="text-xs text-muted-foreground">{suggestion.value}</p>
                </button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setWizardOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
